import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert, Stack,
} from '@mui/material';
import { Print, PictureAsPdf, GridOn, Refresh, CheckCircle, ErrorOutline } from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { reportCashFlowURL } from '../../BaseURL';

function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateLong = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'long', year: 'numeric' });
};

const NAVY  = '#0B2545';
const GREEN = '#0F766E';
const RED   = '#B91C1C';
const GOLD  = '#C89211';
const RULE  = '#0B2545';

export default function CashFlow({ start, end }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const printRef = useRef(null);

  const load = () => {
    if (!start || !end) return;
    setLoading(true); setError('');
    axios.get(reportCashFlowURL, {
      headers: headers(),
      params: { start, end },
    })
      .then(r => { if (r.data.success) setData(r.data); else setError(r.data.message); })
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  // ─── PRINT ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open('', '_blank', 'width=1080,height=820');
    if (!w) return;
    w.document.write(`
      <html><head><title>Cash Flow Summary - ${data?.period.label || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 28px 32px; color: #111; }
        .letterhead { display: flex; align-items: center; gap: 20px; padding-bottom: 14px; border-bottom: 3px double ${NAVY}; }
        .letterhead img { width: 84px; height: 84px; object-fit: contain; border-radius: 6px; }
        .letterhead .info h1 { margin: 0; font-size: 26px; letter-spacing: 0.02em; color: ${NAVY}; }
        .letterhead .info p { margin: 3px 0 0; color: #555; font-size: 12px; }
        .stmt-title { text-align: center; margin: 22px 0 4px; }
        .stmt-title .name { font-size: 15px; font-weight: 800; letter-spacing: 0.16em; color: ${NAVY}; border-top: 1.5px solid ${NAVY}; border-bottom: 1.5px solid ${NAVY}; display: inline-block; padding: 4px 24px; }
        .stmt-title .period { margin-top: 8px; font-size: 12px; color: #555; }
        table { width: 100%; border-collapse: collapse; margin-top: 18px; }
        thead th { text-align: left; font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase; color: #fff; background: ${NAVY}; padding: 9px 12px; }
        thead th.right { text-align: right; }
        tbody td { padding: 6px 12px; font-size: 12.5px; border-bottom: 1px solid #eef0f4; }
        tbody td.right { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .section td { background: ${NAVY}; color: #fff; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; padding: 9px 12px; font-size: 11.5px; }
        .section.out td { background: ${RED}; }
        .section.close td { background: ${GOLD}; }
        .cat td { background: #f4f6fa; color: ${NAVY}; font-weight: 700; padding: 7px 12px; font-size: 12.5px; }
        .cat.out td { background: #fcf0f0; color: #991B1B; }
        .item td { padding-left: 32px; }
        .sub td { border-top: 1px solid #d5dbe4; font-weight: 700; padding-left: 32px; }
        .sec-total td { border-top: 2px solid ${NAVY}; border-bottom: 2px solid ${NAVY}; font-weight: 800; color: ${NAVY}; background: #eef2f7; text-transform: uppercase; font-size: 12.5px; padding: 10px 12px; }
        .sec-total.out td { border-color: #991B1B; color: #991B1B; background: #fcf0f0; }
        .sec-total.close td { border-color: ${GOLD}; color: #6B4A08; background: #FFF5DA; }
        .net td { border-top: 3px double ${NAVY}; border-bottom: 3px double ${NAVY}; font-weight: 800; font-size: 14px; background: #fffdf5; padding: 12px; text-transform: uppercase; letter-spacing: 0.1em; }
        .net.pos td { color: ${GREEN}; }
        .net.neg td { color: ${RED}; }
        .recon-box { margin-top: 18px; padding: 12px; border-radius: 6px; font-weight: 700; text-align: center; }
        .recon-box.ok { background: #e7f5ef; color: ${GREEN}; border: 1px solid ${GREEN}; }
        .recon-box.bad { background: #fdecec; color: ${RED}; border: 1px solid ${RED}; }
        .footer { display: flex; justify-content: space-between; margin-top: 30px; font-size: 10px; color: #888; }
        @page { size: A4; margin: 15mm; }
      </style>
      </head><body>${printRef.current.innerHTML}</body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  // ─── EXPORT PDF ───────────────────────────────────────────────────────────
  const handlePdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 42;

    // Letterhead
    let y = 40;
    const logoSize = 60;
    let textLeft = margin;
    if (data.church?.logo && String(data.church.logo).startsWith('data:image')) {
      try {
        const fmtType = data.church.logo.substring(11, data.church.logo.indexOf(';'));
        doc.addImage(data.church.logo, fmtType.toUpperCase(), margin, y, logoSize, logoSize);
        textLeft = margin + logoSize + 14;
      } catch (e) { /* skip */ }
    }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(11, 37, 69);
    doc.text(data.church?.name || 'Church', textLeft, y + 20);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(90);
    const subInfo = [data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ');
    if (subInfo) doc.text(subInfo, textLeft, y + 36);
    y += Math.max(logoSize, 42) + 8;
    doc.setDrawColor(11, 37, 69); doc.setLineWidth(1.2);
    doc.line(margin, y, pageW - margin, y);
    doc.setLineWidth(0.4); doc.line(margin, y + 3, pageW - margin, y + 3);
    y += 20;

    // Title
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(11, 37, 69);
    const title = 'CASH FLOW SUMMARY';
    const titleW = doc.getTextWidth(title);
    const tx = (pageW - titleW) / 2;
    doc.setLineWidth(0.8);
    doc.line(tx - 18, y - 4, tx + titleW + 18, y - 4);
    doc.text(title, pageW / 2, y + 12, { align: 'center' });
    doc.line(tx - 18, y + 18, tx + titleW + 18, y + 18);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(90);
    doc.text(
      `For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`,
      pageW / 2, y + 34, { align: 'center' }
    );
    y += 46;

    // Build table body
    const body = [];

    // Opening balances
    body.push([{ content: 'OPENING CASH BALANCES', colSpan: 2, styles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold' } }]);
    body.push([{ content: 'Cash Type', styles: { fillColor: [244, 246, 250], textColor: [11, 37, 69], fontStyle: 'bold' } },
               { content: 'Amount (GH₵)', styles: { fillColor: [244, 246, 250], textColor: [11, 37, 69], fontStyle: 'bold', halign: 'right' } }]);
    body.push(['      Cash in Hand', { content: fmt(data.opening.cash_in_hand), styles: { halign: 'right' } }]);
    data.opening.banks.forEach(b => {
      body.push([`      ${b.name}`, { content: fmt(b.balance), styles: { halign: 'right' } }]);
    });
    body.push([
      { content: 'Total Opening Balance', styles: { fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
      { content: fmt(data.opening.total), styles: { halign: 'right', fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
    ]);

    // Inflows
    body.push([{ content: 'CASH INFLOWS (RECEIPTS)', colSpan: 2, styles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold' } }]);
    if (data.inflows.categories.length === 0) {
      body.push([{ content: 'No receipts for this period', colSpan: 2, styles: { fontStyle: 'italic', textColor: 120, halign: 'center' } }]);
    } else {
      data.inflows.categories.forEach(cat => {
        body.push([{ content: cat.name, colSpan: 2, styles: { fillColor: [244, 246, 250], textColor: [11, 37, 69], fontStyle: 'bold' } }]);
        cat.items.forEach(it => {
          body.push([`      ${it.name}`, { content: fmt(it.amount), styles: { halign: 'right' } }]);
        });
        body.push([
          { content: `      Subtotal — ${cat.name}`, styles: { fontStyle: 'bold' } },
          { content: fmt(cat.total), styles: { halign: 'right', fontStyle: 'bold' } },
        ]);
      });
    }
    body.push([
      { content: 'TOTAL INFLOWS', styles: { fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
      { content: fmt(data.inflows.total), styles: { halign: 'right', fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
    ]);

    // Outflows
    body.push([{ content: 'CASH OUTFLOWS (PAYMENTS)', colSpan: 2, styles: { fillColor: [185, 28, 28], textColor: 255, fontStyle: 'bold' } }]);
    if (data.outflows.categories.length === 0) {
      body.push([{ content: 'No payments for this period', colSpan: 2, styles: { fontStyle: 'italic', textColor: 120, halign: 'center' } }]);
    } else {
      data.outflows.categories.forEach(cat => {
        body.push([{ content: cat.name, colSpan: 2, styles: { fillColor: [252, 240, 240], textColor: [153, 27, 27], fontStyle: 'bold' } }]);
        cat.items.forEach(it => {
          body.push([`      ${it.name}`, { content: fmt(it.amount), styles: { halign: 'right' } }]);
        });
        body.push([
          { content: `      Subtotal — ${cat.name}`, styles: { fontStyle: 'bold' } },
          { content: fmt(cat.total), styles: { halign: 'right', fontStyle: 'bold' } },
        ]);
      });
    }
    body.push([
      { content: 'TOTAL OUTFLOWS', styles: { fontStyle: 'bold', fillColor: [252, 240, 240], textColor: [153, 27, 27] } },
      { content: fmt(data.outflows.total), styles: { halign: 'right', fontStyle: 'bold', fillColor: [252, 240, 240], textColor: [153, 27, 27] } },
    ]);

    // Net movement
    const netLabel = data.net_movement >= 0 ? 'NET INCREASE IN CASH' : 'NET DECREASE IN CASH';
    const netColor = data.net_movement >= 0 ? [15, 118, 110] : [185, 28, 28];
    body.push([
      { content: netLabel, styles: { fontStyle: 'bold', fillColor: [255, 253, 245], textColor: netColor, fontSize: 12 } },
      { content: fmt(Math.abs(data.net_movement)), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 253, 245], textColor: netColor, fontSize: 12 } },
    ]);

    // Closing balances
    body.push([{ content: 'CLOSING CASH BALANCES', colSpan: 2, styles: { fillColor: [200, 146, 17], textColor: 255, fontStyle: 'bold' } }]);
    body.push(['      Cash in Hand', { content: fmt(data.closing.cash_in_hand), styles: { halign: 'right' } }]);
    data.closing.banks.forEach(b => {
      body.push([`      ${b.name}`, { content: fmt(b.balance), styles: { halign: 'right' } }]);
    });
    body.push([
      { content: 'Total Closing Balance', styles: { fontStyle: 'bold', fillColor: [255, 245, 218], textColor: [107, 74, 8] } },
      { content: fmt(data.closing.total), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 245, 218], textColor: [107, 74, 8] } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Description', { content: 'Amount (GH₵)', styles: { halign: 'right' } }]],
      body,
      theme: 'grid',
      styles: { fontSize: 9.5, cellPadding: 5, lineColor: [230, 232, 238], lineWidth: 0.4 },
      headStyles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold', halign: 'left', valign: 'middle' },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 150, halign: 'right' },
      },
      margin: { top: 40, left: margin, right: margin, bottom: 80 },
      didDrawPage: () => {
        doc.setFontSize(9); doc.setTextColor(120);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - margin, pageH - 24, { align: 'right' });
        doc.text(`Generated ${new Date().toLocaleString('en-GH')}`, margin, pageH - 24);
      },
    });

    // Reconciliation box
    let ry = doc.lastAutoTable?.finalY || y;
    ry += 20;
    if (ry > pageH - 60) { doc.addPage(); ry = 60; }
    if (data.reconciliation.reconciled) {
      doc.setDrawColor(15, 118, 110); doc.setFillColor(231, 245, 239);
    } else {
      doc.setDrawColor(185, 28, 28); doc.setFillColor(253, 236, 236);
    }
    doc.setLineWidth(0.6);
    doc.roundedRect(margin, ry, pageW - margin * 2, 26, 4, 4, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
    doc.setTextColor(...(data.reconciliation.reconciled ? [15, 118, 110] : [185, 28, 28]));
    const msg = data.reconciliation.reconciled
      ? 'Reconciled — Opening + Net Movement = Closing.'
      : `Reconciliation off by GH₵ ${fmt(Math.abs(data.reconciliation.difference))}`;
    doc.text(msg, pageW / 2, ry + 17, { align: 'center' });

    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    doc.save(`Cash_Flow_${stamp.replace(/[\/\s]/g, '_')}.pdf`);
  };

  // ─── EXPORT EXCEL ─────────────────────────────────────────────────────────
  const handleExcel = () => {
    if (!data) return;
    const aoa = [];
    aoa.push([data.church?.name || 'Church']);
    if (data.church?.address) aoa.push([data.church.address]);
    const contact = [data.church?.phone, data.church?.email].filter(Boolean).join(' · ');
    if (contact) aoa.push([contact]);
    aoa.push([]);
    aoa.push(['CASH FLOW SUMMARY']);
    aoa.push([`For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`]);
    aoa.push([]);
    aoa.push(['Description', 'Amount (GH₵)']);

    aoa.push(['OPENING CASH BALANCES']);
    aoa.push([`   Cash in Hand`, Number(data.opening.cash_in_hand)]);
    data.opening.banks.forEach(b => aoa.push([`   ${b.name}`, Number(b.balance)]));
    aoa.push(['Total Opening Balance', Number(data.opening.total)]);
    aoa.push([]);

    aoa.push(['CASH INFLOWS (RECEIPTS)']);
    if (data.inflows.categories.length === 0) aoa.push(['   No receipts for this period', '']);
    data.inflows.categories.forEach(cat => {
      aoa.push([cat.name]);
      cat.items.forEach(it => aoa.push([`   ${it.name}`, Number(it.amount)]));
      aoa.push([`Subtotal — ${cat.name}`, Number(cat.total)]);
    });
    aoa.push(['TOTAL INFLOWS', Number(data.inflows.total)]);
    aoa.push([]);

    aoa.push(['CASH OUTFLOWS (PAYMENTS)']);
    if (data.outflows.categories.length === 0) aoa.push(['   No payments for this period', '']);
    data.outflows.categories.forEach(cat => {
      aoa.push([cat.name]);
      cat.items.forEach(it => aoa.push([`   ${it.name}`, Number(it.amount)]));
      aoa.push([`Subtotal — ${cat.name}`, Number(cat.total)]);
    });
    aoa.push(['TOTAL OUTFLOWS', Number(data.outflows.total)]);
    aoa.push([]);

    const netLabel = data.net_movement >= 0 ? 'NET INCREASE IN CASH' : 'NET DECREASE IN CASH';
    aoa.push([netLabel, Number(data.net_movement)]);
    aoa.push([]);

    aoa.push(['CLOSING CASH BALANCES']);
    aoa.push([`   Cash in Hand`, Number(data.closing.cash_in_hand)]);
    data.closing.banks.forEach(b => aoa.push([`   ${b.name}`, Number(b.balance)]));
    aoa.push(['Total Closing Balance', Number(data.closing.total)]);
    aoa.push([]);
    aoa.push([data.reconciliation.reconciled
      ? 'Reconciled — Opening + Net Movement = Closing.'
      : `Reconciliation off by GH₵ ${fmt(Math.abs(data.reconciliation.difference))}`]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 50 }, { wch: 20 }];
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: 1 });
      const cell = ws[addr];
      if (cell && typeof cell.v === 'number') cell.z = '#,##0.00';
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cash Flow');
    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    XLSX.writeFile(wb, `Cash_Flow_${stamp.replace(/[\/\s]/g, '_')}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Building cash flow summary for {fmtDateLong(start)} — {fmtDateLong(end)}…
        </Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error" onClose={() => setError('')}>{error}</Alert>;
  if (!data) return null;

  const reconciled = data.reconciliation.reconciled;

  return (
    <Box>
      {/* Action bar */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" mb={2} flexWrap="wrap">
        <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        <Button size="small" variant="outlined" startIcon={<Print />} onClick={handlePrint}>Print</Button>
        <Button size="small" variant="outlined" startIcon={<GridOn />} onClick={handleExcel}
          sx={{ color: '#0F6C3D', borderColor: '#0F6C3D', '&:hover': { borderColor: '#0F6C3D', bgcolor: '#0F6C3D0d' } }}>
          Export Excel
        </Button>
        <Button size="small" variant="contained" startIcon={<PictureAsPdf />} onClick={handlePdf}
          sx={{ bgcolor: RED, '&:hover': { bgcolor: '#991B1B' } }}>
          Export PDF
        </Button>
      </Stack>

      {/* Statement paper */}
      <Paper
        elevation={2}
        sx={{
          border: '1px solid #E2E8F0', borderRadius: 2,
          p: { xs: 3, md: 6 }, bgcolor: '#fff',
          maxWidth: 1040, mx: 'auto',
          boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
        }}
      >
        <Box ref={printRef}>
          {/* Letterhead */}
          <Box className="letterhead" sx={{
            display: 'flex', alignItems: 'center', gap: 3,
            pb: 2, borderBottom: `3px double ${RULE}`,
          }}>
            {data.church?.logo ? (
              <Box component="img" src={data.church.logo} alt="Logo"
                sx={{ width: 90, height: 90, objectFit: 'contain', borderRadius: 1 }} />
            ) : (
              <Box sx={{
                width: 90, height: 90, borderRadius: 1,
                bgcolor: `${NAVY}12`, color: NAVY,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 30,
              }}>
                {(data.church?.name || 'C').charAt(0).toUpperCase()}
              </Box>
            )}
            <Box className="info" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" fontWeight={800}
                sx={{ color: NAVY, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                {data.church?.name || 'Church'}
              </Typography>
              {(data.church?.address || data.church?.phone || data.church?.email) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {[data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ')}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Title block */}
          <Box className="stmt-title" sx={{ textAlign: 'center', mt: 3.5, mb: 0.5 }}>
            <Typography className="name" sx={{
              fontSize: 15, fontWeight: 800, letterSpacing: '0.16em', color: NAVY,
              borderTop: `1.5px solid ${NAVY}`, borderBottom: `1.5px solid ${NAVY}`,
              display: 'inline-block', px: 3, py: 0.5,
            }}>
              CASH FLOW SUMMARY
            </Typography>
            <Typography className="period" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              For the period <b>{fmtDateLong(data.period.start_date)}</b> to <b>{fmtDateLong(data.period.end_date)}</b>
            </Typography>
          </Box>

          {/* Body table */}
          <StatementTable data={data} />

          {/* Reconciliation pill */}
          <Box sx={{
            mt: 2.5, p: 1.5, borderRadius: 1.5, textAlign: 'center',
            border: `1px solid ${reconciled ? GREEN : RED}`,
            bgcolor: reconciled ? '#e7f5ef' : '#fdecec',
            color:   reconciled ? GREEN : RED,
            fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
          }}>
            {reconciled ? <CheckCircle sx={{ fontSize: 20 }} /> : <ErrorOutline sx={{ fontSize: 20 }} />}
            <Typography component="span" fontWeight={700}>
              {reconciled
                ? 'Reconciled — Opening + Net Movement = Closing.'
                : `Reconciliation off by GH₵ ${fmt(Math.abs(data.reconciliation.difference))}`}
            </Typography>
          </Box>

          {/* Footer */}
          <Box className="footer" sx={{
            display: 'flex', justifyContent: 'space-between', mt: 4,
            fontSize: 10, color: '#888',
          }}>
            <span>Generated {new Date().toLocaleString('en-GH')}</span>
            <span>{data.church?.name || ''}</span>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Inner rendered table ──────────────────────────────────────────────────
function StatementTable({ data }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 18 }}>
      <thead>
        <tr>
          <th style={thStyle}>Description</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Amount (GH₵)</th>
        </tr>
      </thead>
      <tbody>
        {/* OPENING BALANCES */}
        <tr className="section"><td colSpan={2} style={sectionHeadStyle(NAVY)}>OPENING CASH BALANCES</td></tr>
        <tr className="cat">
          <td colSpan={2} style={catRowStyle(NAVY, '#f4f6fa')}>Cash & Bank Positions</td>
        </tr>
        <tr className="item">
          <td style={itemStyle}>Cash in Hand</td>
          <td style={amountStyle}>{fmt(data.opening.cash_in_hand)}</td>
        </tr>
        {data.opening.banks.map((b, i) => (
          <tr key={`op-${i}`} className="item">
            <td style={itemStyle}>{b.name}</td>
            <td style={amountStyle}>{fmt(b.balance)}</td>
          </tr>
        ))}
        <tr className="sec-total">
          <td style={sectionTotalLabelStyle(NAVY, '#eef2f7')}>Total Opening Balance</td>
          <td style={sectionTotalAmountStyle(NAVY, '#eef2f7')}>{fmt(data.opening.total)}</td>
        </tr>

        <tr><td colSpan={2} style={{ height: 16 }} /></tr>

        {/* INFLOWS */}
        <tr className="section"><td colSpan={2} style={sectionHeadStyle(NAVY)}>CASH INFLOWS (RECEIPTS)</td></tr>
        {data.inflows.categories.length === 0 ? (
          <tr><td colSpan={2} style={noteStyle}>No receipts for this period</td></tr>
        ) : (
          data.inflows.categories.map(cat => (
            <React.Fragment key={`in-${cat.name}`}>
              <tr className="cat">
                <td colSpan={2} style={catRowStyle(NAVY, '#f4f6fa')}>{cat.name}</td>
              </tr>
              {cat.items.map((it, i) => (
                <tr key={`in-${cat.name}-${i}`} className="item">
                  <td style={itemStyle}>{it.name}</td>
                  <td style={amountStyle}>{fmt(it.amount)}</td>
                </tr>
              ))}
              <tr className="sub">
                <td style={subtotalLabelStyle}>Subtotal — {cat.name}</td>
                <td style={subtotalAmountStyle}>{fmt(cat.total)}</td>
              </tr>
            </React.Fragment>
          ))
        )}
        <tr className="sec-total">
          <td style={sectionTotalLabelStyle(NAVY, '#eef2f7')}>TOTAL INFLOWS</td>
          <td style={sectionTotalAmountStyle(NAVY, '#eef2f7')}>{fmt(data.inflows.total)}</td>
        </tr>

        <tr><td colSpan={2} style={{ height: 16 }} /></tr>

        {/* OUTFLOWS */}
        <tr className="section"><td colSpan={2} style={sectionHeadStyle(RED)}>CASH OUTFLOWS (PAYMENTS)</td></tr>
        {data.outflows.categories.length === 0 ? (
          <tr><td colSpan={2} style={noteStyle}>No payments for this period</td></tr>
        ) : (
          data.outflows.categories.map(cat => (
            <React.Fragment key={`out-${cat.name}`}>
              <tr className="cat out">
                <td colSpan={2} style={catRowStyle('#991B1B', '#fcf0f0')}>{cat.name}</td>
              </tr>
              {cat.items.map((it, i) => (
                <tr key={`out-${cat.name}-${i}`} className="item">
                  <td style={itemStyle}>{it.name}</td>
                  <td style={amountStyle}>{fmt(it.amount)}</td>
                </tr>
              ))}
              <tr className="sub">
                <td style={subtotalLabelStyle}>Subtotal — {cat.name}</td>
                <td style={subtotalAmountStyle}>{fmt(cat.total)}</td>
              </tr>
            </React.Fragment>
          ))
        )}
        <tr className="sec-total">
          <td style={sectionTotalLabelStyle('#991B1B', '#fcf0f0')}>TOTAL OUTFLOWS</td>
          <td style={sectionTotalAmountStyle('#991B1B', '#fcf0f0')}>{fmt(data.outflows.total)}</td>
        </tr>

        <tr><td colSpan={2} style={{ height: 16 }} /></tr>

        {/* NET MOVEMENT */}
        <tr className={`net ${data.net_movement >= 0 ? 'pos' : 'neg'}`}>
          <td style={netLabelStyle(data.net_movement)}>
            {data.net_movement >= 0 ? 'NET INCREASE IN CASH' : 'NET DECREASE IN CASH'}
          </td>
          <td style={netAmountStyle(data.net_movement)}>{fmt(Math.abs(data.net_movement))}</td>
        </tr>

        <tr><td colSpan={2} style={{ height: 16 }} /></tr>

        {/* CLOSING BALANCES */}
        <tr className="section close"><td colSpan={2} style={sectionHeadStyle(GOLD)}>CLOSING CASH BALANCES</td></tr>
        <tr className="cat">
          <td colSpan={2} style={catRowStyle('#6B4A08', '#FFF5DA')}>Cash & Bank Positions</td>
        </tr>
        <tr className="item">
          <td style={itemStyle}>Cash in Hand</td>
          <td style={amountStyle}>{fmt(data.closing.cash_in_hand)}</td>
        </tr>
        {data.closing.banks.map((b, i) => (
          <tr key={`cl-${i}`} className="item">
            <td style={itemStyle}>{b.name}</td>
            <td style={amountStyle}>{fmt(b.balance)}</td>
          </tr>
        ))}
        <tr className="sec-total close">
          <td style={sectionTotalLabelStyle('#6B4A08', '#FFF5DA')}>Total Closing Balance</td>
          <td style={sectionTotalAmountStyle('#6B4A08', '#FFF5DA')}>{fmt(data.closing.total)}</td>
        </tr>
      </tbody>
    </table>
  );
}

// ─── Inline styles ─────────────────────────────────────────────────────────
const thStyle = {
  textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
  color: '#fff', background: NAVY, padding: '10px 12px', fontWeight: 700,
};
const itemStyle    = { padding: '6px 12px', fontSize: 13.5, borderBottom: '1px solid #eef0f4', paddingLeft: 32, color: '#1e293b' };
const amountStyle  = { padding: '6px 12px', fontSize: 13.5, borderBottom: '1px solid #eef0f4',
                       textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                       fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#1e293b' };
const sectionHeadStyle = (color) => ({
  background: color, color: '#fff', fontWeight: 800, letterSpacing: '0.1em',
  padding: '9px 12px', fontSize: 12, textTransform: 'uppercase',
});
const catRowStyle = (fg, bg) => ({
  background: bg, color: fg, fontWeight: 700, padding: '7px 12px', fontSize: 13,
  borderBottom: '1px solid #e5e9ef',
});
const noteStyle = {
  padding: '10px 12px', fontStyle: 'italic', color: '#999',
  fontSize: 12.5, textAlign: 'center', borderBottom: '1px solid #eef0f4',
};
const subtotalLabelStyle = {
  padding: '7px 12px', fontSize: 13, borderTop: '1px solid #d5dbe4',
  fontWeight: 700, paddingLeft: 32, color: NAVY,
};
const subtotalAmountStyle = {
  padding: '7px 12px', fontSize: 13, borderTop: '1px solid #d5dbe4',
  fontWeight: 700, textAlign: 'right', color: NAVY,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};
const sectionTotalLabelStyle = (fg, bg) => ({
  padding: '10px 12px', fontSize: 13, background: bg, color: fg, fontWeight: 800,
  borderTop: `2px solid ${fg}`, borderBottom: `2px solid ${fg}`, textTransform: 'uppercase',
});
const sectionTotalAmountStyle = (fg, bg) => ({
  padding: '10px 12px', fontSize: 13, background: bg, color: fg, fontWeight: 800,
  textAlign: 'right', borderTop: `2px solid ${fg}`, borderBottom: `2px solid ${fg}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
});
const netLabelStyle = (val) => ({
  padding: '14px 12px', fontSize: 15, fontWeight: 800, background: '#fffdf5',
  color: val >= 0 ? GREEN : RED,
  borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  textTransform: 'uppercase', letterSpacing: '0.1em',
});
const netAmountStyle = (val) => ({
  padding: '14px 12px', fontSize: 15, fontWeight: 800, textAlign: 'right', background: '#fffdf5',
  color: val >= 0 ? GREEN : RED,
  borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
});
